// Job 52: the Expected Invoice could be recalculated but never corrected by hand,
// so a charge the calculation got wrong had no route to the right number.
//
// Editing it raises a second problem worth being explicit about: four paths in the
// bill run recalculate and save the expected invoice (the Recalculate button,
// auto-populating kWh, applying NEM analysis, and parsing an invoice). Any of them
// would overwrite a manual correction without a word, so an edited result carries
// `overridden_at` and callers check `isShadowOverridden` before replacing it.

import type { ShadowBillLine, ShadowBillResult, ShadowBillSection } from '../types/billTypes';

const GST_RATE = 0.1;
const round2 = (n: number) => parseFloat(n.toFixed(2));

/** True when these values were corrected by hand and must not be silently replaced. */
export const isShadowOverridden = (
  result: ShadowBillResult | null | undefined,
): boolean => !!result?.overridden_at;

/**
 * Section totals, subtotal, GST and total recomputed from edited lines.
 *
 * GST: an explicit GST line wins — including an explicit 0 (a GST-free bill) — and
 * only a result with NO gst line at all falls back to 10% of the subtotal. This is a
 * calculated shadow bill, so the 10% prediction stays as a fallback; but once a gst
 * line exists its value is honoured verbatim, a credit or a deliberate 0 included.
 */
export const recalcShadowTotals = (
  lines: ShadowBillLine[],
  base: ShadowBillResult,
): ShadowBillResult => {
  const section_totals: Record<ShadowBillSection, number> = {
    retail: 0, distribution: 0, metering: 0, environmental: 0, other: 0, gst: 0,
  };
  for (const line of lines) {
    if (line.section in section_totals) {
      section_totals[line.section] = round2((section_totals[line.section] ?? 0) + (Number(line.amount) || 0));
    }
  }

  const subtotal = round2(
    section_totals.retail + section_totals.distribution
    + section_totals.metering + section_totals.environmental + section_totals.other,
  );
  // An explicit gst line (even one summing to 0) is honoured; only the total
  // absence of a gst line falls back to the 10% prediction.
  const hasGstLine = lines.some((l) => l.section === 'gst');
  const gst = hasGstLine ? section_totals.gst : round2(subtotal * GST_RATE);
  const total = round2(subtotal + gst);

  return { ...base, lines, section_totals, subtotal, gst, total };
};

// Deliberately no "derive amount from volume × rate" helper here. Rate units vary
// across the shadow lines (c/kWh for usage, dollars for daily and fixed charges),
// and a helper that guessed wrong would produce confidently incorrect money. The
// editor takes the amount directly; volume and rate stay editable as the record of
// how it was reached.
